import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { isNumeric } from '../../utils/Numeric';
import {
  message, Modal,
  Affix,
  Checkbox, Select, Button,
} from 'antd';
import {
  UploadOutlined,
  ExclamationCircleOutlined,
  CheckCircleTwoTone,
  DiffOutlined,
  SyncOutlined
} from '@ant-design/icons';

import { Page } from '../../components';
import {
  getGoodsBySpecName as getGoodsTableBySpecName,
  getGoodsByName as getGoodsTableByName
} from '../../services/Goods';

export default React.forwardRef((props, ref) => {
  const { reload, onCloseCallback, goodsCategory, insertGoods, updateGoods, insertGoodsSpec, updateGoodsSpec } = props;

  const columns = ['#', '品名', '英文名称', '中文名称', '产品号前缀', '产品号', '尺寸', '重量(g)', '备注']
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [isChecking, setChecking] = useState(false);
  const [isSyncing, setSyncing] = useState(false);

  function sleep(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  const syncGoodsInfoDB = (callback) => {
    const key = 'update_goods';
    const temp = [].concat(rows);
    const insert = temp.filter(x => x._goods_status === -2).map(y => ({
      idx: y.idx,
      name_en: y.name_en,
      name_cn: y.name_cn,
      prefix: y.prefix,
      category_id: y.category_id
    }));
    const update = temp.filter(x => x._goods_status === -21).map(y => ({
      idx: y.idx,
      id: y.id,
      name_en: y.name_en,
      name_cn: y.name_cn,
      prefix: y.prefix,
      category_id: y.category_id
    }));

    const arr = [];
    insert.forEach((x, i) => {
      arr.push(
        new Promise(async (resolve, reject) => {
          try {
            await insertGoods(x).then(async data => {
              message.loading({ content: `正在同步${x.name_en}...`, key });
              temp[x.idx]._goods_status = 0;
              temp[x.idx].id = data.id;
              resolve(true);
            })
          } catch (e) {
            resolve(false);
          }
        })
      );
    });

    update.forEach(async (x, i) => {
      arr.push(
        new Promise(async (resolve, reject) => {
          try {
            await updateGoods(x.id, x).then(data => {
              message.loading({ content: `正在同步${x.name_en}...`, key });
              temp[x.idx]._goods_status = 0;
              resolve(true);
            });
          } catch (e) {
            resolve(false);
          }
        })
      );
    });
    Promise.all(arr).then(values => {
      rows.forEach((x, i) => {
        if (x.name_en.length === 0 && i > 0) {
          rows[i].id = rows[i - 1].id;
        }
      });
      message.success({ content: `产品校验完成 [ ${values.length}] 条数据  `, key, duration: 1 });
      callback && callback();
    });
  }
  const syncGoodsDB = () => {
    syncGoodsInfoDB(() => {
      syncGoodsSpecDB(() => {
        const temp = [].concat(rows);
        setRows(temp);
        reload && reload();
      })
    })
  }

  const syncGoodsSpecDB = async (callback) => {
    const key = 'update_goods';
    const temp = [].concat(rows);
    const insert = temp.filter(x => x._status === 1).map(y => ({
      idx: y.idx,
      goods_id: y.id,
      name: y.goods_spec_name,
      size: y.size,
      weight: y.weight,
      remark: y.remark
    }));
    const update = temp.filter(x => x._status === 2).map(y => ({
      idx: y.idx,
      id: y._goods_spec_id,
      goods_id: y.id,
      name: y.goods_spec_name,
      size: y.size,
      weight: y.weight,
      remark: y.remark
    }));

    const insertArr = [];
    insert.forEach(x => {
      const find = insertArr.find(y => y.goods_id === x.goods_id);
      if (find) {
        find.specs.push(x);
      } else {
        insertArr.push({
          goods_id: x.goods_id,
          goods_name: x.name_en,
          specs: [x]
        });
      }
    });

    const updateArr = [];
    update.forEach(x => {
      const find = updateArr.find(y => y.goods_id === x.goods_id);
      if (find) {
        find.specs.push(x);
      } else {
        updateArr.push({
          goods_id: x.goods_id,
          goods_name: x.name_en,
          specs: [x]
        });
      }
    });

    const arr = [];
    insertArr.forEach(x => {
      arr.push(
        new Promise((resolve, reject) => {
          insertGoodsSpec(x.goods_id, x.specs).then(data => {
            message.loading({ content: `正在同步${x.goods_name}...`, key });
            x.specs.forEach(ss => {
              temp[ss.idx]._status = 0;
            });
            resolve(true)
          })
        })
      );
    });
    updateArr.forEach(x => {
      arr.push(
        new Promise((resolve, reject) => {
          updateGoodsSpec(x.goods_id, x.specs).then(data => {
            message.loading({ content: `正在同步${x.goods_name}...`, key });
            x.specs.forEach(ss => {
              temp[ss.idx]._status = 0;
            });
            resolve(true);
          })
        })
      );
    });
    Promise.all(arr).then(values => {
      message.success({ content: `产品同步完成 [ ${values.length}] 条数据  `, key, duration: 2 });
      callback && callback();
    });
  }

  const initRows = () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const no = (row[columns[0]] + '').trim();
      const category = (row[columns[1]] || '').trim();
      const nameEn = (row[columns[2]] || '').trim();
      const nameZn = (row[columns[3]] || '').trim();
      const prefix = (row[columns[4]] + '').trim();
      const spec = (row[columns[5]] + '').trim();
      const size = (row[columns[6]] + '').trim();
      const weight = (row[columns[7]] + '').trim();
      const remark = (row[columns[8]] + '').trim();
      row.idx = i;
      row.name_en = nameEn;
      row.name_cn = nameZn;
      row.category = category;
      row.prefix = prefix;
      if (prefix.length === 0) {
        row.prefix = (rows[i - 1] || {}).prefix;
      }
      row.goods_spec_name = spec;
      row.weight = weight;
      row.size = size;
      row.remark = remark;
      row._goods_status = undefined;
      if (spec.length === 0) {
        row._status = -1;
      } else {
        row._status = undefined;
      }
    }
  }

  const checkGoodsByNameEn = (callback) => {
    const arr = [];
    const temp = rows.filter(x => x._status === undefined && x.name_en);
    const key = 'update_goods';
    temp.forEach(x => {
      const name = x.name_en;
      x.category_id = (goodsCategory.find(c => c.name === x.category) || {}).id || -1;
      arr.push(
        new Promise(async (resolve, reject) => {
          try {
            await getGoodsTableByName(window.btoa(name)).then(data => {
              message.loading({ content: `正在校对${name}...`, key });
              x.id = data.id;
              if (x.name_cn !== data.name_cn || x.prefix !== data.prefix) {
                x._goods_status = -21;
                x._name_cn = data.name_cn;
                x._prefix = data.prefix;
              } else {
                x._goods_status = 0;
              }
              resolve(true)
            });
          } catch (error) {
            x._goods_status = -2;
            resolve(false);
          }
        })
      );
    })
    Promise.all(arr).then(values => {
      message.success({ content: `产品校验完成 [ ${values.length}] 条数据  `, key, duration: 1 });
      callback && callback();
    })
  }

  const checkGoodsSpecByName = (callback) => {
    const arr = [];
    const temp = rows.filter(x => x._status === undefined);
    const key = 'update_goods';
    temp.forEach(x => {
      const name = x.goods_spec_name;
      name.length > 0 && arr.push(
        new Promise(async (resolve, reject) => {
          try {
            // 1. check goods table
            await getGoodsTableBySpecName(name).then(async data => {
              message.loading({ content: `正在校对${name}...`, key });
              x._goods_spec_id = data.id;
              if (x.weight !== (data.weight + '') || x.size !== (data.size + '')) {
                x._status = 2;
                x._weight = data.weight + '';
                x._size = data.size + '';
              } else {
                x._status = 0;
              }
              resolve(true);
            });
          } catch (error) {
            x._status = 1;
            resolve(false);
          }
        })
      );
    })
    Promise.all(arr).then(values => {
      message.success({ content: `产品校验完成 [ ${values.length}] 条数据  `, key, duration: 1 });
      callback && callback();
    })
  }

  const checkGoodsDB = async () => {
    setChecking(true);
    initRows();
    checkGoodsByNameEn(() => {
      checkGoodsSpecByName(() => {
        const temp = [].concat(rows);
        setRows(temp);
        setChecking(false);
      });
    })
  }

  const onImportExcel = file => {
    // 获取上传的文件对象
    const { files } = file.target;
    if (files.length === 0) return;
    setFileName(files[0].name)
    // 通过FileReader对象读取文件
    const fileReader = new FileReader();
    fileReader.onload = event => {
      try {
        const { result } = event.target;

        // 以二进制流方式读取得到整份excel表格对象
        const workbook = XLSX.read(result, { type: 'binary' });
        // 存储获取到的数据
        let data = [];
        // 遍历每张工作表进行读取（这里默认只读取第一张表）
        for (const sheet in workbook.Sheets) {
          // esline-disable-next-line
          if (workbook.Sheets.hasOwnProperty(sheet)) {
            // 利用 sheet_to_json 方法将 excel 转成 json 数据
            console.log(sheet)
            data = data.concat(XLSX.utils.sheet_to_json(workbook.Sheets[sheet]));
            setRows(data);
            break; // 如果只取第一张表，就取消注释这行
          }
        }
        // 最终获取到并且格式化后的 json 数据
        message.success('导出文件完成！')
        console.log(data);
      } catch (e) {
        // 这里可以抛出文件类型错误不正确的相关提示
        message.error(e);
      }
    };
    // 以二进制方式打开文件
    fileReader.readAsBinaryString(files[0]);
  }

  const parseHeader = () => {
    const tds = [];
    columns.forEach(x => {
      tds.push(<th>{x}</th>);
    })
    return <tr>{tds}</tr>
  }

  const parseRow = (row, i) => {
    console.log(i, row)
    const no = (row[columns[0]] + '').trim();
    const category = (row[columns[1]] || '').trim();
    const nameEn = (row[columns[2]] || '').trim();
    const nameZn = (row[columns[3]] || '').trim();
    const specPrefix = (row[columns[4]] + '').trim();
    const spec = (row[columns[5]] + '').trim();
    const size = (row[columns[6]] + '').trim();
    const weight = (row[columns[7]] + '').trim();
    const remark = (row[columns[8]] + '').trim();

    const tds = [];
    //tds.push(<td className="td"><Checkbox onChange={(e) => onSelected(e.target.checked, i)} checked={row._selected}>{i + 1}</Checkbox></td>);
    columns.forEach((x, idx) => {
      if (idx === 2) {
        if (row._goods_status === -2) {
          tds.push(<td className="td">{row[x]} <span style={{ color: '#F50' }}>产品库不存在</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else if (idx === 3) {
        if (row._goods_status === -21 && row[x] != row._name_cn) {
          tds.push(<td className="td">{row[x]} <span style={{ color: '#F50' }}>不一致({row._name_cn})</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else if (idx === 4) {
        if (row._goods_status === -21 && row[x] != row._prefix) {
          tds.push(<td className="td">{row[x]} <span style={{ color: '#F50' }}>不一致({row._prefix})</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else if (idx === 5) {
        if (row._status === 0) {
          tds.push(<td className="td">{row[x]} <CheckCircleTwoTone twoToneColor="#52c41a" /></td>);
        } else if (row._status === -1) {
          tds.push(<td className="td">{row[x]} <span style={{ color: 'red' }}>无效数据</span></td>);
        } else if (row._status === 1) {
          tds.push(<td className="td">{row[x]} <span style={{ color: '#F50' }}>产品库不存在</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else if (idx === 6) {
        if (row._size !== size && row._status === 2) {
          tds.push(<td className="td">{row[x]} <span style={{ color: 'red' }}>不一致({row._size})</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else if (idx === 7) {
        if (row._weight !== weight && row._status === 2) {
          tds.push(<td className="td">{row[x]} <span style={{ color: 'red' }}>不一致({row._weight})</span></td>);
        } else {
          tds.push(<td className="td">{row[x]}</td>);
        }
      } else {
        tds.push(<td className="td">{row[x]}</td>);
      }
    });
    return <tr>{tds}</tr>
  }

  return (
    <div className='goods-import-container'>
      <div className="goods-import-button-group">
        <div className='goods-import-file-name'> {fileName} </div>
        <div icon={<UploadOutlined />} className='upload-wrap'>
          <input className='file-uploader' type='file' accept='.xlsx, .xls' onChange={onImportExcel} />
          <span className='upload-text'>选择文件...</span>
        </div>
        <div className='upload-tip'>支持 .xlsx、.xls文件</div>

        <div style={{ marginLeft: 'auto' }}></div>
        {
          fileName && <Button loading={isChecking} disabled={isSyncing} icon={<DiffOutlined />} type="primary" onClick={() => checkGoodsDB()}>
            产品信息比对
          </Button>
        }
        {
          fileName && <Button loading={isSyncing} disabled={isChecking || (rows.filter(x => x._status > 0).length === 0 && rows.filter(x => x._goods_status === -2 || x._goods_status === -21).length === 0)} icon={<SyncOutlined />} type="primary" onClick={() => syncGoodsDB()}>
            产品信息同步
          </Button>
        }
      </div>
      <div className="goods-import-content">
        <table id="import-table" border="1" className="goods_table">
          {
            rows.length > 0 && parseHeader(rows[0])
          }
          {
            rows.map((x, i) => parseRow(x, i))
          }
        </table>
      </div>
    </div >
  );
});